import { siteOrigin, structuredData, markdownPath } from './pages.mjs';

export function headTags(page, { noindex = false } = {}) {
  const image = siteOrigin + (page.image || '/assets/social/home.png');
  const tags = [
    { key: 'title', tag: 'title', text: page.title },
    { key: 'description', tag: 'meta', attrs: { name: 'description', content: page.description } },
    { key: 'robots', tag: 'meta', attrs: { name: 'robots', content: noindex || page.index === false ? 'noindex,follow' : 'index,follow,max-image-preview:large' } },
    ...Object.entries({
      title: page.title, description: page.description, site_name: 'MoDeck',
      type: page.name === 'Review' && page.index !== false ? 'article' : 'website',
      ...(page.path ? { url: siteOrigin + page.path } : {}),
      image, 'image:width': '1200', 'image:height': '630', 'image:type': 'image/png',
      'image:alt': page.imageAlt || 'MoDeck: After Effects template automation for teams.',
    }).map(([name, content]) => ({ key: `og:${name}`, tag: 'meta', attrs: { property: `og:${name}`, content } })),
    ...Object.entries({ card: 'summary_large_image', title: page.title, description: page.description, image, 'image:alt': page.imageAlt || 'MoDeck: After Effects template automation for teams.' })
      .map(([name, content]) => ({ key: `twitter:${name}`, tag: 'meta', attrs: { name: `twitter:${name}`, content } })),
  ];
  if (page.path) tags.push(
    { key: 'canonical', tag: 'link', attrs: { rel: 'canonical', href: siteOrigin + page.path } },
    { key: 'markdown', tag: 'link', attrs: { rel: 'alternate', type: 'text/markdown', href: siteOrigin + markdownPath(page) } },
    { key: 'llms', tag: 'link', attrs: { rel: 'describedby', type: 'text/plain', href: `${siteOrigin}/llms.txt` } },
  );
  const schema = structuredData(page);
  if (schema) tags.push({ key: 'schema', tag: 'script', attrs: { type: 'application/ld+json' }, text: JSON.stringify(schema).replace(/</g, '\\u003c') });
  return tags;
}

export function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
}

export function renderHead(page, options) {
  return headTags(page, options).map(({ key, tag, attrs = {}, text = '' }) => {
    const attributes = Object.entries({ 'data-modeck-seo': key, ...attrs }).map(([name, value]) => ` ${name}="${escapeHtml(value)}"`).join('');
    return `<${tag}${attributes}>` + (['meta', 'link'].includes(tag) ? '' : `${tag === 'script' ? text : escapeHtml(text)}</${tag}>`);
  }).join('\n');
}

// Own only SEO tags. Keep analytics, icons, styles, and application scripts intact.
// The same keys are emitted into the build, so direct loads and SPA navigation agree.
export function applyHead(document, page, options) {
  const wanted = new Set();
  for (const { key, tag, attrs = {}, text = '' } of headTags(page, options)) {
    wanted.add(key);
    const existing = [...document.head.querySelectorAll(`[data-modeck-seo="${key}"]`)];
    let element = existing.shift();
    existing.forEach(duplicate => duplicate.remove());
    if (!element) {
      element = document.createElement(tag);
      element.setAttribute('data-modeck-seo', key);
      document.head.appendChild(element);
    }
    for (const [name, value] of Object.entries(attrs)) element.setAttribute(name, value);
    if (tag !== 'meta' && tag !== 'link') element.textContent = text;
  }
  document.head.querySelectorAll('[data-modeck-seo]').forEach(element => {
    if (!wanted.has(element.getAttribute('data-modeck-seo'))) element.remove();
  });
}
